/**
 * Semafor (traffic light) indicator for service tasks.
 * - StatusIndicator: deadline-based (červená/oranžová/zelená) – stĺpec status_indicator; farby z module_settings (tikety).
 * - MachineStateDot: machine_state pre Error/Need – pred názvom stroja; farby pevné (nie z nastavení).
 */
import React, { useContext } from 'react';
import { DateTime } from 'luxon';
import { getCrmStatusIndicatorColors, getServiceStatusIndicatorColors } from '../utils/moduleSettings';
import { LocaleContext } from './LocaleContext';

const CLOSED_STATUS_IDS = [28, 18, 30]; // UZAVRENY, NESCHVALENY, STORNOVANY

// Farby bodiek pre stroj (MachineStateDot) – nepoužívajú module_settings
const ORANGE_CLASS = 'bg-[#ffa500]';
export const STATUS_ORANGE_HEX = '#ffa500';
const GREEN_OK_CLASS = 'bg-[#059669]';
const GREEN_OK_HEX = '#059669';

/**
 * @typedef {'maintenance' | 'crm'} StatusIndicatorSettingsModule
 */

/** @param {StatusIndicatorSettingsModule} [settingsModule] */
function readDaysToDeadline(moduleSettings, settingsModule = 'maintenance') {
    const daysSetting = (moduleSettings || []).find(
        (s) => s.module === settingsModule && s.key === 'days_to_deadline'
    );
    return parseInt(daysSetting?.value || '5', 10) || 5;
}

/** Vráti 'red' | 'orange' | 'green' | null podľa termínu (len pre tikety). */
function getDeadlineState(deadline, daysToDeadline) {
    if (!deadline) return null;
    const today = DateTime.now().startOf('day');
    const daysUntilDeadline = Math.floor(deadline.diff(today, 'days').days);
    if (daysUntilDeadline < 0) return 'red';
    if (daysUntilDeadline <= daysToDeadline) return 'orange';
    return 'green';
}

/**
 * Stĺpec status_indicator – len deadline; vráti stav 'red'|'orange'|'green' alebo null.
 * @param {object} [options]
 * @param {StatusIndicatorSettingsModule} [options.settingsModule]
 * @param {string} [options.deadlineField]
 */
export function getStatusIndicatorColor(task, moduleSettings = [], options = {}) {
    const settingsModule = options.settingsModule ?? 'maintenance';
    const deadlineField = options.deadlineField ?? 'deadline';
    const daysToDeadline = readDaysToDeadline(moduleSettings, settingsModule);
    if (settingsModule === 'crm') {
        if (task?.cancelled_at) return null;
    } else if (CLOSED_STATUS_IDS.includes(task?.status_id)) {
        return null;
    }
    const rawDeadline = task?.[deadlineField] ?? task?.deadline;
    const deadline = rawDeadline ? DateTime.fromISO(String(rawDeadline)) : null;
    return getDeadlineState(deadline?.isValid ? deadline : null, daysToDeadline);
}

/** Vráti "red" | "orange" | "green" | "" podľa stavu indikátora (pre header filter). */
export function getStatusIndicatorFilterValue(task, moduleSettings = [], options = {}) {
    const state = getStatusIndicatorColor(task, moduleSettings, options);
    return state || '';
}

/** Stĺpec status_indicator – len deadline (preklad cez t v komponente). */
function resolveStatusIndicatorTitle(task, moduleSettings, t, options = {}) {
    const settingsModule = options.settingsModule ?? 'maintenance';
    const deadlineField = options.deadlineField ?? 'deadline';
    const daysToDeadline = readDaysToDeadline(moduleSettings, settingsModule);
    if (settingsModule === 'crm') {
        if (task?.cancelled_at) return '';
    } else if (CLOSED_STATUS_IDS.includes(task?.status_id)) {
        return '';
    }

    const rawDeadline = task?.[deadlineField] ?? task?.deadline;
    const deadline = rawDeadline ? DateTime.fromISO(String(rawDeadline)) : null;
    if (!deadline?.isValid) return '';

    const today = DateTime.now().startOf('day');
    const daysUntilDeadline = Math.floor(deadline.diff(today, 'days').days);

    if (daysUntilDeadline < 0) return t('ServiceTask.StatusIndicator.Overdue');
    if (daysUntilDeadline <= daysToDeadline) return t('ServiceTask.StatusIndicator.DueSoon');
    return t('ServiceTask.StatusIndicator.OnTrack');
}

/** Bodka pre machine_state – Error/Need; ak nie je stav zadaný, predvolene Funkčný */
export function getMachineStateColor(task) {
    if (task?.type !== 'Error' && task?.type !== 'Need') return null;
    const state = task?.machine_state;
    if (state === 'in_fault') return 'bg-red-500';
    if (state === 'working_limited') return ORANGE_CLASS;
    return GREEN_OK_CLASS; // functional alebo nezančený stav = Funkčný
}

function resolveMachineStateTitle(task, t) {
    if (task?.type !== 'Error' && task?.type !== 'Need') return '';
    const state = task?.machine_state;
    if (state === 'in_fault') return t('ServiceTask.MachineState.InFault');
    if (state === 'working_limited') return t('ServiceTask.MachineState.WorkingLimited');
    return t('ServiceTask.MachineState.Functional');
}

const dotBaseClass = 'inline-block w-4 h-4 rounded-full mr-2 flex-shrink-0';

function machineDotStyle(color) {
    if (color === ORANGE_CLASS) return { backgroundColor: STATUS_ORANGE_HEX };
    if (color === GREEN_OK_CLASS) return { backgroundColor: GREEN_OK_HEX };
    return undefined;
}

/**
 * t / locale z props: pri renderi cez react-tabulator (ReactDOM.render do bunky) nie je k dispozícii LocaleContext,
 * preto môžu nadradené zoznamy predávať t a locale z closure.
 */
export function MachineStateDot({ task, t: tProp }) {
    const ctx = useContext(LocaleContext);
    const t = tProp ?? ctx.t;
    const color = getMachineStateColor(task);
    const title = resolveMachineStateTitle(task, t);
    if (!color) return null;
    const useStyle = color === ORANGE_CLASS || color === GREEN_OK_CLASS;
    return (
        <span
            className={useStyle ? dotBaseClass : `${dotBaseClass} ${color}`}
            style={machineDotStyle(color)}
            title={title}
        />
    );
}

/** Bodka pre indikátor termínu tiketu – farby z module_settings (Všeobecné nastavenia). */
export default function StatusIndicator({
    task,
    moduleSettings,
    t: tProp,
    settingsModule = 'maintenance',
    deadlineField = 'deadline',
}) {
    const ctx = useContext(LocaleContext);
    const t = tProp ?? ctx.t;
    const indicatorOptions = { settingsModule, deadlineField };
    const state = getStatusIndicatorColor(task, moduleSettings, indicatorOptions);
    const title = resolveStatusIndicatorTitle(task, moduleSettings, t, indicatorOptions);
    if (!state) return null;
    const colors = settingsModule === 'crm'
        ? getCrmStatusIndicatorColors(moduleSettings || [])
        : getServiceStatusIndicatorColors(moduleSettings || []);
    const hex = colors[state] || (state === 'red' ? '#dc2626' : state === 'orange' ? '#ca8a04' : '#16a34a');
    return (
        <span
            className={dotBaseClass}
            style={{ backgroundColor: hex }}
            title={title}
        />
    );
}
